import React from "react";

/* The drawing-sheet ground: tinted paper with a two-weight graph grid ruled
   onto it, drawn with gradients rather than an image asset.

   This is the project's single definition of that ground. Any surface that
   wants graph paper renders this component rather than declaring the
   gradients itself, so two surfaces cannot drift apart: there is one place
   the ruling is decided and everything reads it.

       <SheetGround>…</SheetGround>                    the vellum sheet
       <SheetGround as="section" minor={12} major={60}>…</SheetGround>
       <SheetGround theme={BLUEPRINT}>…</SheetGround>  another material

   The ruling is applied after any `style` a caller passes, so type and
   colour can be added but the grid itself cannot be quietly redefined. A
   surface that needs a different ruling changes `minor`/`major`/`theme` -
   the three inputs the component actually supports - rather than reaching
   past it with background properties of its own.

   The invariants below are pinned by SheetGround.test.tsx. Both were live
   defects once; neither is visible in a diff. */

export interface SheetGroundTheme {
  ground: string;
  gridMinor: string;
  gridMajor: string;
}

/* The vellum ground's own colours, and the pitch of its ruling.

   These live here, with the component that draws them, rather than in the
   folio theme: the theme spreads them instead of restating them, so the
   ground has exactly one definition in the project. */
export const SHEET_VELLUM: SheetGroundTheme = {
  ground: "#EDF0EE",
  gridMinor: "#DBE3E0",
  gridMajor: "#C6D4D0",
};

export const SHEET_MINOR = 10;
export const SHEET_MAJOR = 50;

/* Minor ruling every `minor` px, major every `major` px, both 1px wide.

   MAJOR IS LISTED FIRST, and must stay first. In CSS the first background
   image paints on top. Every major line sits at a multiple of `major`,
   which is a multiple of `minor`, so a minor line lands on exactly the same
   pixel. List the minor layers first and they cover the major ones at full
   opacity - the sheet loses its heavy rules and flattens into a uniform
   mesh with no hierarchy at all.

   Pairs with sheetGridSize: layer order here and size order there must
   match, which is why they are never used apart. Prefer sheetGridStyle. */
export const sheetGridImage = (t: SheetGroundTheme = SHEET_VELLUM): string =>
  [
    `linear-gradient(to right, ${t.gridMajor} 0 1px, transparent 1px)`,
    `linear-gradient(to bottom, ${t.gridMajor} 0 1px, transparent 1px)`,
    `linear-gradient(to right, ${t.gridMinor} 0 1px, transparent 1px)`,
    `linear-gradient(to bottom, ${t.gridMinor} 0 1px, transparent 1px)`,
  ].join(",");

/* TILED, NOT A REPEATING GRADIENT. A repeating-linear-gradient is rasterised
   once across the whole box, so at a fractional device-pixel ratio the
   rounding error accumulates and the spacing visibly beats across the width
   - and two boxes of different sizes rule differently from identical CSS.
   One period per tile lets the tiling engine snap each cell, so the ruling
   stays even at any zoom, any DPR and any box size. */
export const sheetGridSize = (minor: number = SHEET_MINOR, major: number = SHEET_MAJOR): string =>
  [`${major}px ${major}px`, `${major}px ${major}px`, `${minor}px ${minor}px`, `${minor}px ${minor}px`].join(",");

/* The whole ground as a style object, for a surface that already owns its
   element and cannot wrap it. Returns the image and its tile size together,
   because either one alone is wrong. */
export const sheetGridStyle = (
  theme: SheetGroundTheme = SHEET_VELLUM,
  minor: number = SHEET_MINOR,
  major: number = SHEET_MAJOR
): React.CSSProperties => ({
  background: theme.ground,
  backgroundImage: sheetGridImage(theme),
  backgroundSize: sheetGridSize(minor, major),
  /* Anchored to the box origin so the ruling starts on a whole cell. */
  backgroundPosition: "0 0",
  backgroundRepeat: "repeat",
});

export interface SheetGroundProps extends React.HTMLAttributes<HTMLElement> {
  /* Defaults to the vellum sheet. Pass another material to re-rule it. */
  theme?: SheetGroundTheme;
  minor?: number;
  major?: number;
  /* The element to draw the ground on - section, main, header, aside. */
  as?: React.ElementType;
}

const SheetGround: React.FC<SheetGroundProps> = ({
  theme = SHEET_VELLUM,
  minor = SHEET_MINOR,
  major = SHEET_MAJOR,
  as: Tag = "div",
  style,
  children,
  ...rest
}) => (
  <Tag {...rest} style={{ ...style, ...sheetGridStyle(theme, minor, major) }}>
    {children}
  </Tag>
);

export default SheetGround;
